import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Tab, Tabs, Table } from "react-bootstrap";
import swal from "sweetalert";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../../../assets/global/url";
import Select from "react-select";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import useDecodeToken from "../../../../../hooks/customHook/useDecodeToken";
import { useQueryParams } from "../../../../../hooks/customHook/useQueryParams";
import { formatToTwoDecimal } from "../../../../../utils/numberFormatter";

const NewIncomeStatement = () => {
  const userLoggedID = useDecodeToken();

  // For query parameters
  const queryParameterList = ["module", "id", "fromDate", "toDate"];
  const queryParameters = useQueryParams(queryParameterList);
  const { module, id, fromDate, toDate } = queryParameters;

  // For cutoff filter
  const [cutoffList, setCutoffList] = useState([]);
  const [selectedCutoff_id, setSelectedCutoff_id] = useState("");
  const [thisFromdate, setThisFromdate] = useState("");
  const [thisTodate, setThisTodate] = useState("");

  // States per section
  const [revenue, setRevenue] = useState({});
  const [costOfGoodsSold, setCostOfGoodsSold] = useState({});
  const [others, setOthers] = useState({
    totalOperatingExpense: 0,
    depreciation: 0,
    foreignExchangeGainLoss: 0,
    foreignExchangeSales: 0,
    foreignExchangePayable: 0,
    foreignExchangePayableAfterPayment: 0,
    foreignExchangeExpenses: 0,
  });

  // For gross sales profit
  const totalRevenue = revenue?.totalRevenue;
  const totalCostOfGoodsSold = costOfGoodsSold.costOfGoodsSold;
  const grossSalesProfit = totalRevenue - totalCostOfGoodsSold; // Total gross sales profit

  // For net income
  const expenses = others?.totalOperatingExpense;
  const depreciation = others?.depreciation;
  const totalExpenses = expenses + depreciation; // Total expenses
  const foreignExchangeGainLoss = others?.foreignExchangeGainLoss;
  const foreignExchangeSales = others?.foreignExchangeSales;
  const foreignExchangePayable = others?.foreignExchangePayable;
  const foreignExchangePayableAfterPayment =
    others?.foreignExchangePayableAfterPayment;
  const foreignExchangeExpenses = others?.foreignExchangeExpenses;
  const netForeignExchangeGainLoss =
    foreignExchangeGainLoss +
    foreignExchangeSales -
    foreignExchangePayable -
    foreignExchangeExpenses;
  // const netForeignExchangeGainLoss = (foreignExchangePayable - foreignExchangeSales) + foreignExchangeGainLoss;
  const hasNoIncome = [
    totalRevenue,
    costOfGoodsSold.openingInventory,
    costOfGoodsSold.purchases,
    costOfGoodsSold.adjustments,
  ].every((number) => !number);
  const netIncome =
    grossSalesProfit -
    totalExpenses +
    netForeignExchangeGainLoss +
    foreignExchangePayableAfterPayment; // Net income total amount

  // Get the default cutoff
  const getCutoff = async () => {
    await axios
      .get(`${BASE_URL}/cutoff/getCutoffs`)
      .then((res) => {
        if (!res.data.length) return; // No cutoff records yet

        const [{ id: cutoffId, from: startDate, to: endDate }] = res.data; // Destructure the first item in array

        // Use query parameters if provided; otherwise, fall back to default values
        const defaultCutoffId = id || cutoffId;
        const cutoffStartDate = fromDate || startDate;
        const cutoffEndDate = toDate || endDate;

        setCutoffList(res.data);
        setSelectedCutoff_id(defaultCutoffId);
        setThisFromdate(cutoffStartDate);
        setThisTodate(cutoffEndDate);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Handle change cutoff
  const handleCutoffChange = (value) => {
    setSelectedCutoff_id(value);
    const cutoff_fromdate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).from ||
      "";
    const cutoff_todate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).to || "";

    setThisFromdate(cutoff_fromdate);
    setThisTodate(cutoff_todate);
  };

  // Get income statement summary
  const getIncomeStatement = async () => {
    try {
      const incomeStatementEndpoint = `${BASE_URL}/incomeReport`;
      const params = {
        startDate: thisFromdate,
        endDate: thisTodate,
      };

      const [revenue, cogs, others] = await Promise.allSettled([
        axios.get(`${incomeStatementEndpoint}/revenue`, { params }),
        axios.get(`${incomeStatementEndpoint}/cost-of-goods-sold`, { params }),
        axios.get(`${incomeStatementEndpoint}/others`, { params }),
      ]);

      setRevenue(revenue.value.data);
      setCostOfGoodsSold(cogs.value.data);

      setOthers({
        totalOperatingExpense: others.value.data.totalOperatingExpense,
        depreciation: others.value.data.depreciation,
        foreignExchangeGainLoss: others.value.data.foreignExchangeGainLoss,
        foreignExchangeSales: others.value.data.foreignExchangeSales,
        foreignExchangePayable: others.value.data.foreignExchangePayable,
        foreignExchangePayableAfterPayment:
          others.value.data.foreignExchangePayableAfterPayment,
        foreignExchangeExpenses: others.value.data.foreignExchangeExpenses,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (thisFromdate && thisTodate) getIncomeStatement();
  }, [thisFromdate, thisTodate]);

  useEffect(() => {
    getCutoff();
  }, []);

  // Dev note para sa calculations
  const title = `
     Dev note:
     \n--- Revenue ---
     \n• Sales Revenue = total ng sales invoice kasama yung mga discounts pero hindi idadagdag yung moisture,
     status: not equal sa rejected tsaka pending.
     \n• Sales Discount = total ng lahat ng discount
     \n• Other Income = total ng lahat ng approved na other income
     \n• Total Revenue = Sales Revenue - Sales Discount - Other Income
     \n--- Cost of Goods Sold ---
     \n• Opening inventory = Debit - Credit ng inventory journal, less than startDate
     \n• Purchases = total ng lahat ng payable, status: not equal sa pending tsaka rejected
     \n• Close Inventory = Debit - Credit ng inventory journal, less than or equal endDate
     \n• Cost of Goods Sold = Opening Inventory + Purchases - Close Inventory
     \n• Gross Sale's Profit = Total Revenue - Cost of Goods Sold
     \n--- Others ---
     \n• Expenses = for total expenses
     \n• Depreciation = fixed asset depreciation
     \n• Foreign Exchange Gain/Loss (Funds) = Forex ng transfer money to other currency
     \n• Foreign Exchange Gain/Loss (Sales) = Forex ng Sales, system rate - sales rate
     \n• Foreign Exchange Gain/Loss (Payable) = Forex ng Payable, system rate - payable rate
    `;

  const totalSalesDiscount =
    revenue.salesDiscount + revenue.totalDiscountFromClosedCollection;
  const totalPurchaseDiscount = costOfGoodsSold.totalDiscountFromClosedPurchase;

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3 text-uppercase">
            {module && id && fromDate && toDate && module === "cutoff" && (
              <Link
                to={`/accounting/view-cutoff/${id}`}
                className="text-dark mx-2"
              >
                <i class="fa-solid fa-arrow-left"></i>
              </Link>
            )}
            Income Statement
          </span>
        </div>
        {userLoggedID === "11111111-1111-1111-1111-111111111111" && (
          <div>
            <i
              className="fa-solid fa-circle-info d-inline-flex align-items-center text-primary fs-6 ms-2 mb-1"
              style={{ cursor: "pointer" }}
              title={title}
            ></i>
          </div>
        )}
      </div>

      {/* Cutoff filter */}
      <div className="w-100 row mx-auto mt-2">
        <h6>Accounting Period</h6>
        <div className="col-sm mb-2">
          <span>Cutoff</span>
          <Form.Select
            value={selectedCutoff_id}
            onChange={(e) => handleCutoffChange(e.target.value)}
            className="form-select"
            onMouseDown={(e) => {
              if (cutoffList.length === 0) {
                e.preventDefault();
                swal({
                  icon: "warning",
                  title: "No Cutoff Found",
                  text: "No cutoff records found. Please create a cutoff first in Monthly Cutoff Module.",
                });
                return;
              }
            }}
          >
            {cutoffList.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-sm mb-2">
          <span>From</span>
          <DatePicker
            selected={thisFromdate}
            dateFormat="MMM/dd/yyyy"
            className="form-control"
            readOnly
          />
        </div>
        <div className="col-sm mb-2">
          <span>To</span>
          <DatePicker
            selected={thisTodate}
            dateFormat="MMM/dd/yyyy"
            className="form-control"
            readOnly
          />
        </div>
        <div className="col-sm"></div>
      </div>

      <div className="container-fluid">
        <div className="w-100 d-flex align-items-center mt-4">
          <h5>Income Statement</h5>
          <hr className="flex-grow-1 mx-3" />
        </div>
        <div className="container">
          <div className="table-responsive">
            <table className="table table-bordered mt-4">
              <thead>
                <tr>
                  <th>Subject 1</th>
                  <th>Subject 2</th>
                  <th className="text-end">Amount (₱)</th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue section */}
                <tr>
                  <td>Revenue</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td>Sales Revenue</td>
                  <td className="text-end">
                    {formatToTwoDecimal(revenue.salesRevenue)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Sales Discount</td>
                  <td
                    className={`text-end ${totalSalesDiscount < 0 ? "text-success" : totalSalesDiscount > 0 ? "text-danger" : ""}`}
                  >
                    {formatToTwoDecimal(totalSalesDiscount)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Other Income</td>
                  <td className="text-end">
                    {formatToTwoDecimal(revenue.otherIncome)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Re-Open</td>
                  <td className="text-end">
                    {formatToTwoDecimal(revenue.reOpenSales)}
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Total Revenue:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(revenue.totalRevenue)}
                    </strong>
                  </td>
                </tr>

                {/* Cost of goods sold section */}
                <tr>
                  <td>Cost of Goods Sold (COGS)</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td>Opening Inventory</td>
                  <td className="text-end">
                    {formatToTwoDecimal(costOfGoodsSold.openingInventory)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Purchases</td>
                  <td className="text-end">
                    {formatToTwoDecimal(costOfGoodsSold.purchases)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Purchases Discount</td>
                  <td
                    className={`text-end ${totalPurchaseDiscount > 0 ? "text-success" : totalPurchaseDiscount < 0 ? "text-danger" : ""}`}
                  >
                    {formatToTwoDecimal(totalPurchaseDiscount)}
                  </td>
                </tr>
                {/* commented totalAdjustments kasi nagcacause ng redundancy dahil kasama na sa closing inventory yung adjustment */}
                {/* <tr>
                  <td></td>
                  <td>Inventory Adjustments</td>
                  <td className="text-end">
                    {formatToTwoDecimal(costOfGoodsSold.adjustments)}
                  </td>
                </tr> */}
                <tr>
                  <td></td>
                  <td>Close Inventory</td>
                  <td className="text-end">
                    {formatToTwoDecimal(costOfGoodsSold.closeInventory)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Re-Open</td>
                  <td className="text-end">
                    {formatToTwoDecimal(costOfGoodsSold.reOpenPurchase)}
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Cost of Goods Sold:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(costOfGoodsSold.costOfGoodsSold)}
                    </strong>
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Gross Sale's Profit:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(grossSalesProfit)}
                    </strong>
                  </td>
                </tr>

                {/* Others section*/}
                <tr>
                  <td>Others</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td>Expenses</td>
                  <td className="text-end">
                    {formatToTwoDecimal(others?.totalOperatingExpense)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Depreciation</td>
                  <td className="text-end">
                    {formatToTwoDecimal(others?.depreciation)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Foreign Exchange Gain/Loss (Funds)</td>
                  <td
                    className={`text-end ${others.foreignExchangeGainLoss > 0 ? "text-success" : others.foreignExchangeGainLoss < 0 ? "text-danger" : ""}`}
                  >
                    {formatToTwoDecimal(others?.foreignExchangeGainLoss)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Foreign Exchange Gain/Loss (Sales)</td>
                  <td
                    className={`text-end ${others.foreignExchangeSales > 0 ? "text-success" : others.foreignExchangeSales < 0 ? "text-danger" : ""}`}
                  >
                    {formatToTwoDecimal(others?.foreignExchangeSales)}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Foreign Exchange Gain/Loss (Payable)</td>
                  <td
                    className={`text-end ${
                      others.foreignExchangePayable * -1 > 0
                        ? "text-success"
                        : others.foreignExchangePayable * -1 < 0
                          ? "text-danger"
                          : ""
                    }`}
                  >
                    {/* {formatToTwoDecimal(others?.foreignExchangePayable)} */}
                    {/* 
                     - ginawang ganito para if ang value is positive dapat mag display as negative
                     - kasi if ang foreign exchange for PO is and a DOllar and bumaba ang rate ng dollar nakaka gain sila kaya dapat positive
                    */}
                    {formatToTwoDecimal(
                      others.foreignExchangePayable * -1 +
                        others.foreignExchangePayableAfterPayment,
                    )}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Foreign Exchange Gain/Loss (Expenses)</td>
                  <td
                    className={`text-end ${
                      others.foreignExchangeExpenses * -1 > 0
                        ? "text-success"
                        : others.foreignExchangeExpenses * -1 < 0
                          ? "text-danger"
                          : ""
                    }`}
                  >
                    {/* {formatToTwoDecimal(others?.foreignExchangeExpenses)} */}
                    {/* 
                     - ginawang ganito para if ang value is positive dapat mag display as negative
                     - kasi if ang foreign exchange for PO is and a DOllar and bumaba ang rate ng dollar nakaka gain sila kaya dapat positive
                    */}
                    {formatToTwoDecimal(others.foreignExchangeExpenses * -1)}
                  </td>
                </tr>

                {/* Expense type section */}
                {/* {expenseType?.list?.map((typeOne) => (
                  <React.Fragment key={typeOne.expenses_one_id}>
                    <tr>
                      <td>{typeOne.expenses_type_one}</td>
                      <td></td>
                      <td></td>
                    </tr>
                    {typeOne.expenses2s?.map((typeTwo) => (
                      <tr key={typeTwo.id}>
                        <td></td>
                        <td>{typeTwo.sub_type}</td>
                        <td className="text-end">
                          {formatToTwoDecimal(typeTwo.expenseSubTypeAmount)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Total Operating Expenses:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(expenseType.totalOperatingExpense)}
                    </strong>
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Income Before Tax:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(incomeBeforeTax)}
                    </strong>
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Income Tax Expenses:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">TBA</strong>
                  </td>
                </tr> */}
                <tr className="table-secondary">
                  <td></td>
                  <td>
                    <strong>Net Income:</strong>
                  </td>
                  <td className="text-end">
                    <strong className="peso">
                      {formatToTwoDecimal(hasNoIncome ? 0 : netIncome)}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewIncomeStatement;
